import { Box, Container, Stack, Typography } from '@mui/material';
import { getDetailedContactView, getSavedTranscript, handleCallTranscriptOutcome } from 'API/linkedin-funnel';
import { CompanyAbout, ContactAbout, ContactDetails } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { useUserData } from 'hooks/use-user-data';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Assessment } from 'types/analysis-result.types';
import { ProfileValues } from 'types/profile.types';
import CallTranscriptHeader from './call-transcript-header';
import ContactDetailView from './contact-detail-view';
import Transcript from './transcript';

const CallTranscriptUI: React.FC = () => {
  const [searchParams] = useSearchParams();

  const { token, language } = useUserData();
  const { t } = useTranslation();

  const profileId = searchParams.get('profileId');
  const tenant = searchParams.get('tenant');
  const funnelId = searchParams.get('funnelId');
  const stepIndex = searchParams.get('stepIndex');

  const [transcript, setTranscript] = useState<string>('');
  const [transcriptLoading, setTranscriptLoading] = useState(true);
  const [outcomeMessage, setOutcomeMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [contact, setContact] = useState<{
    loading: boolean;
    aiJudgement: string | undefined;
    aiJudgementExplanation: string | undefined;
    assessment: Assessment | undefined;
    percentiles: ProfileValues | undefined;
    contactDetails: ContactDetails | null;
    aboutContact: ContactAbout | null;
    aboutCompany: CompanyAbout | null;
  }>({
    loading: true,
    aiJudgement: undefined,
    aiJudgementExplanation: undefined,
    assessment: undefined,
    percentiles: undefined,
    contactDetails: null,
    aboutContact: null,
    aboutCompany: null,
  });

  useEffect(() => {
    const fetchTranscript = async () => {
      if (!token || !profileId || !tenant || !funnelId || !stepIndex) {
        setErrorMessage(t('missingParametersUrl'));
        setTranscriptLoading(false);
        return;
      }

      const res = await getSavedTranscript(profileId, tenant, funnelId, stepIndex, language || 'en');

      if (res.error) {
        setErrorMessage(t('failedToLoadTranscript'));
      } else {
        setTranscript(res.transcript || t('noTranscriptFound'));
      }

      setTranscriptLoading(false);
    };

    const fetchContactDetails = async () => {
      if (!token || !profileId || !tenant || !funnelId || !stepIndex) {
        setErrorMessage(t('missingParametersUrl'));
        setContact((prev) => ({ ...prev, loading: false }));
        return;
      }

      setContact((prev) => ({ ...prev, loading: true }));

      const res = await getDetailedContactView(undefined, profileId, funnelId);

      if (res.error) {
        setErrorMessage(t('failedToLoadContactDetails'));
      } else {
        setContact((prev) => ({
          ...prev,
          aiJudgement: res.aiJudgement,
          aiJudgementExplanation: res.aiJudgementExplanation,
          assessment: res.assessment,
          percentiles: res.percentiles,
          contactDetails: res.contactDetails,
          aboutContact: res.aboutContact,
          aboutCompany: res.aboutCompany,
        }));
      }

      setContact((prev) => ({ ...prev, loading: false }));
    };

    fetchTranscript();
    fetchContactDetails();
  }, [token, language, profileId, tenant, funnelId, stepIndex]);

  const handleOutcomeClick = (chosenOutcome: string) => {
    return async () => {
      if (!token || !profileId || !tenant || !funnelId || !stepIndex) {
        setOutcomeMessage(t('missingParameters'));
        return;
      }

      setOutcomeMessage(t('processingChoice'));

      const result = await handleCallTranscriptOutcome(profileId, tenant, chosenOutcome, funnelId, stepIndex);

      if (result.error) {
        setOutcomeMessage(t('failedToRecord'));
      } else {
        setOutcomeMessage(t('thankYouResponse', { chosenOutcome }));
      }
    };
  };

  if (errorMessage) {
    return (
      <Container maxWidth="sm" style={{ marginTop: '40px' }}>
        <Typography variant="h5" align="center" gutterBottom>
          {t('error')}
        </Typography>
        <Typography variant="body1" align="center">
          {errorMessage}
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <CallTranscriptHeader outcomeMessage={outcomeMessage} isLoading={transcriptLoading} handleOutcomeClick={handleOutcomeClick} />
      {outcomeMessage && (
        <div style={{ background: '#FFF', padding: '32px 20px 16px 20px' }}>
          <Typography variant="body1" align="center" style={{ color: 'green' }}>
            {outcomeMessage}
          </Typography>
        </div>
      )}
      <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} mt={2} mb={2}>
        <Box flex={1}>
          <Transcript loading={transcriptLoading} transcript={transcript} />
        </Box>
        <Box flex={1}>
          <ContactDetailView
            loading={contact.loading}
            aiJudgement={contact.aiJudgement}
            aiJudgementExplanation={contact.aiJudgementExplanation}
            assessment={contact.assessment}
            percentiles={contact.percentiles}
            contactDetails={contact.contactDetails}
            aboutContact={contact.aboutContact}
            aboutCompany={contact.aboutCompany}
          />
        </Box>
      </Stack>
    </>
  );
};

export default CallTranscriptUI;
